import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { PostfachCard } from "@/components/postfach/PostfachCard";
import { IdeaPickerSection } from "@/components/dashboard/IdeaPickerSection";
import { TicketDetailOverlay } from "@/components/board/TicketDetailOverlay";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { t } from "@/lib/strings";
import type { MySubmissionItem, UUID } from "@/lib/types";

export function IdeenPage() {
  const isDesktop = useBreakpoint("md");
  const [openTicket, setOpenTicket] = useState<{ boardId: UUID; rowId: UUID } | null>(null);
  const query = useQuery({
    queryKey: queryKeys.mySubmissions,
    queryFn: () => api.listMySubmissions(),
  });
  // Non-members hold no read access to the Ideenbox board's rows — the only ideas
  // a user can see cheaply are the ones they submitted (their own guest grant).
  const ideas: MySubmissionItem[] = (query.data ?? []).filter((s) => s.board_kind === "ideas");

  return (
    <div
      style={{
        flex: 1,
        overflow: "auto",
        padding: isDesktop ? "28px 36px 80px" : "16px 12px 80px",
        background: "var(--bg)",
      }}
    >
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <div style={{ marginBottom: 10 }}>
          <Breadcrumb items={[{ label: t.navHome, to: "/" }, { label: t.ideenPageTitle }]} />
        </div>

        <h1
          style={{
            margin: "0 0 4px",
            fontSize: 24,
            fontWeight: 600,
            color: "var(--ink-1)",
            letterSpacing: "-0.015em",
          }}
        >
          {t.ideenPageTitle}
        </h1>
        <p style={{ margin: "0 0 22px", fontSize: 13, color: "var(--ink-3)" }}>
          {t.ideenPageSubtitle}
        </p>

        {/* Submit doorway — reuses the Home Ideenbox picker; renders nothing when
            no Ideenbox is published to Home. */}
        <IdeaPickerSection />

        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ink-1)",
            letterSpacing: "-0.01em",
            margin: "0 0 12px",
          }}
        >
          {t.ideenMineHeading}
        </div>

        {ideas.length === 0 ? (
          <div
            style={{
              fontSize: 13,
              color: "var(--ink-3)",
              padding: "18px 16px",
              border: "1px dashed var(--hairline-strong)",
              borderRadius: 10,
              background: "var(--surface)",
            }}
          >
            {t.ideenMineEmpty}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ideas.map((it) => (
              <PostfachCard
                key={it.row_id}
                item={it}
                isDesktop={isDesktop}
                onOpen={(boardId, rowId) => setOpenTicket({ boardId, rowId })}
              />
            ))}
          </div>
        )}
      </div>

      {openTicket ? (
        <TicketDetailOverlay
          boardId={openTicket.boardId}
          rowId={openTicket.rowId}
          onClose={() => setOpenTicket(null)}
        />
      ) : null}
    </div>
  );
}
